import { Loader2 } from "lucide-react";
import { JarvisTab } from "./admin/JarvisTab";
import { AdminSupportTab } from "@/components/admin/AdminSupportTab";
import { TaskDashboardTab } from "@/components/admin/TaskDashboardTab";
import { ObservabilityTab } from "@/components/admin/ObservabilityTab";
import { DRTab } from "./admin/DRTab";
import { SystemMetricsTab } from "./admin/SystemMetricsTab";
import { InvestorDashboardTab } from "./admin/InvestorDashboardTab";
import { SystemHealthDashboard } from "./admin/SystemHealthDashboard";
import { AIUsageAdminTab } from "./admin/AIUsageAdminTab";
import { EventMonitorTab } from "./admin/EventMonitorTab";
import { RestaurantManagementTab } from "./admin/RestaurantManagementTab";
import AICommandCenterTab from "./admin/AICommandCenterTab";
import { SimoneWhatsAppTab } from "./admin/SimoneWhatsAppTab";
import { usePlatformAdminAuth } from "@/hooks/usePlatformAdminAuth";
import { usePlatformAdminPanel } from "@/hooks/usePlatformAdminPanel";
import { AdminLoginPage } from "./admin/components/AdminLoginPage";
import { AdminHeader } from "./admin/components/AdminHeader";
import { AdminTabNav } from "./admin/components/AdminTabNav";
import { OverviewTab } from "./admin/components/OverviewTab";
import { RestaurantsTab } from "./admin/components/RestaurantsTab";
import { AffiliatesTab } from "./admin/components/AffiliatesTab";
import { PartnersApplicationsTab } from "./admin/components/PartnersApplicationsTab";
import { LeadsTab } from "./admin/components/LeadsTab";
import { SettingsTab } from "./admin/components/SettingsTab";
import { PlatformAuditTab } from "./admin/components/PlatformAuditTab";
import { SystemErrorsTab } from "./admin/SystemErrorsTab";

export default function PlatformAdminPage() {
  const auth = usePlatformAdminAuth();

  if (!auth.isAuthenticated) {
    return (
      <AdminLoginPage
        loginEmail={auth.loginEmail}
        setLoginEmail={auth.setLoginEmail}
        loginPassword={auth.loginPassword}
        setLoginPassword={auth.setLoginPassword}
        loginLoading={auth.loginLoading}
        handleLogin={auth.handleLogin}
      />
    );
  }

  return (
    <AdminPanel
      adminAxios={auth.adminAxios}
      adminUser={auth.adminUser}
      onLogout={auth.handleLogout}
    />
  );
}

function AdminPanel({ adminAxios, adminUser, onLogout }) {
  const {
    tab, setTab,
    overview,
    filteredRestaurants,
    affiliates,
    leads,
    settings, setSettings,
    loading,
    search, setSearch,
    saving,
    selectedRestaurant,
    restaurantDetail,
    detailLoading,
    impersonating,
    auditResult,
    auditing,
    fixing,
    blocking,
    loginAsRestaurant,
    runAudit,
    runFix,
    toggleBlock,
    openRestaurantDetail,
    closeDetail,
    saveSettings,
    refreshTab,
  } = usePlatformAdminPanel({ adminAxios, onLogout });

  return (
    <div className="min-h-screen bg-background" data-testid="platform-admin">
      <AdminHeader adminUser={adminUser} onLogout={onLogout} />
      <AdminTabNav tab={tab} setTab={setTab} />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          {tab === "overview" && <OverviewTab overview={overview} />}
          {tab === "command_center" && <AICommandCenterTab adminAxios={adminAxios} />}
          {tab === "simone" && <SimoneWhatsAppTab adminAxios={adminAxios} />}
          {tab === "investors" && <InvestorDashboardTab adminAxios={adminAxios} />}
          {tab === "jarvis" && <JarvisTab adminAxios={adminAxios} />}
          {tab === "ai_usage" && <AIUsageAdminTab adminAxios={adminAxios} />}
          {tab === "events" && <EventMonitorTab adminAxios={adminAxios} />}
          {tab === "dr" && <DRTab adminAxios={adminAxios} />}
          {tab === "metrics" && <SystemMetricsTab adminAxios={adminAxios} />}
          {tab === "health" && <SystemHealthDashboard adminAxios={adminAxios} />}
          {tab === "suporte" && <AdminSupportTab adminAxios={adminAxios} />}
          {tab === "tasks" && <TaskDashboardTab adminAxios={adminAxios} />}
          {tab === "observability" && <ObservabilityTab adminAxios={adminAxios} />}
          {tab === "restaurant_mgmt" && <RestaurantManagementTab adminAxios={adminAxios} />}
          {tab === "restaurants" && (
            <RestaurantsTab
              adminAxios={adminAxios}
              filteredRestaurants={filteredRestaurants}
              search={search}
              setSearch={setSearch}
              selectedRestaurant={selectedRestaurant}
              restaurantDetail={restaurantDetail}
              detailLoading={detailLoading}
              impersonating={impersonating}
              auditing={auditing}
              fixing={fixing}
              blocking={blocking}
              auditResult={auditResult}
              onOpenDetail={openRestaurantDetail}
              onCloseDetail={closeDetail}
              onLoginAs={loginAsRestaurant}
              onToggleBlock={toggleBlock}
              onRunAudit={runAudit}
              onRunFix={runFix}
              onRefreshTab={refreshTab}
            />
          )}
          {tab === "affiliates" && (
            <AffiliatesTab
              adminAxios={adminAxios}
              affiliates={affiliates}
              onRefreshTab={refreshTab}
            />
          )}
          {tab === "partner_apps" && <PartnersApplicationsTab adminAxios={adminAxios} />}
          {tab === "leads" && <LeadsTab leads={leads} />}
          {tab === "errors" && <SystemErrorsTab adminAxios={adminAxios} />}
          {tab === "auditoria" && <PlatformAuditTab adminAxios={adminAxios} />}
          {tab === "settings" && (
            <SettingsTab
              settings={settings}
              setSettings={setSettings}
              saving={saving}
              onSave={saveSettings}
            />
          )}
        </div>
      )}
    </div>
  );
}
